import { useMutation } from '@apollo/client';

import { BulkAssignPurchaseOrder, BulkAssignPurchaseOrderVariables }
  from '@/graphql/contracts/_generated_/bulkAssignPurchaseOrder.mutation';
import BulkAssignPurchaseOrderMutation from '@/graphql/contracts/bulkAssignPurchaseOrder.mutation.gql';
import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import { notificationService } from '@/services/notification/notification.client';
import GetPaginatedFilteredVariableContractLinesQuery from
  '@/graphql/contracts/getPaginatedFilteredVariableContractLines.query.gql';
import { ALL_ITEMS } from '@/config/common.config';
import GetBCContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';

export function useBulkAssignPurchaseOrder(refreshGrid: boolean, contractId?: UUID, variableSelected?: boolean) {
  const [bulkAssignPurchaseOrderMutation, { loading }] =
    useMutation<BulkAssignPurchaseOrder, BulkAssignPurchaseOrderVariables>(
      BulkAssignPurchaseOrderMutation, {
        client: apolloClientContracts,
        awaitRefetchQueries: true,
        ...(refreshGrid ? {
          refetchQueries: [
            {
              query: GetBCContractLinesQuery,
              variables: {
                filter: {
                  contractId: { eq: contractId }
                },
                first: ALL_ITEMS
              }
            },
            ...(variableSelected ? [
              {
                query: GetPaginatedFilteredVariableContractLinesQuery,
                variables: {
                  filter: { headerPrimaryKey: { eq: contractId } },
                  first: ALL_ITEMS,
                }
              }
            ] : [])
          ]
        } : {}),

        onError: (err) => notificationService.open({
          title: 'Error',
          text: err.message,
          type: 'danger'
        })
      });

  const bulkAssignPurchaseOrder = async (
    purchaseOrderId: string,
    contractLineIds: string[],
    handleSuccess?: () => void
  ) => {
    try {
      const result = await bulkAssignPurchaseOrderMutation({
        variables: {
          contractLineIds,
          purchaseOrderId,
        },
      });

      if (result.data?.bulkAssignPurchaseOrder &&
        'errorMessage' in result.data.bulkAssignPurchaseOrder) {
        notificationService.open({
          title: 'Error',
          text: result.data.bulkAssignPurchaseOrder.errorMessage,
          type: 'danger',
        });
      }
      else if (!result.errors) {
        if (handleSuccess) {
          handleSuccess();
        }
        notificationService.open({
          title: 'Success',
          text: 'Purchase Order assigned successfully',
          type: 'success',
        });
      }
    } catch (error) {
      console.error('Error assigning purchase order:', error);
    }
  };

  return { bulkAssignPurchaseOrder, loading };
}
